import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAppSelector } from '@/app/hooks';
import {
  deleteKnowledgeSource,
  fetchKnowledgeSources,
  reingestKnowledgeSource,
  uploadKnowledgeDocument,
  uploadKnowledgeUrl,
  type AdminKnowledgeSource,
} from '@/services/adminService';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';

const isAllowedDoc = (file: File): boolean => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'docx' || ext === 'txt' || ext === 'md' || ext === 'pdf';
};

const formatDate = (value: string): string => {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const statusBadgeClassName = (status: AdminKnowledgeSource['status']): string => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-emerald-100 text-emerald-800';
    case 'DELETED':
      return 'bg-slate-100 text-slate-700';
    case 'FAILED':
      return 'bg-rose-100 text-rose-700';
    case 'IN_PROGRESS':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const AdminKnowledgeBasePage = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);

  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [urlValue, setUrlValue] = useState('');
  const [sources, setSources] = useState<ReadonlyArray<AdminKnowledgeSource>>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isUploadingUrl, setIsUploadingUrl] = useState(false);
  const [pendingDeleteSource, setPendingDeleteSource] = useState<AdminKnowledgeSource | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeActionSourceId, setActiveActionSourceId] = useState<string | null>(null);
  const [reingestingIds, setReingestingIds] = useState<Set<string>>(new Set());

  const isAdmin = user?.role === 'ADMIN';
  const hasInProgressSources = useMemo(
    () => sources.some((source) => source.status === 'IN_PROGRESS'),
    [sources],
  );

  const loadSources = useCallback(async () => {
    try {
      setIsLoading(true);
      const nextSources = await fetchKnowledgeSources();
      setSources(nextSources);
    } catch (err) {
      logging.error('Failed to load knowledge sources', { error: err });
      setError('Failed to load uploaded sources.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  useEffect(() => {
    if (!hasInProgressSources) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void loadSources();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [hasInProgressSources, loadSources]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Admin access required</CardTitle>
            <CardDescription>You do not have permission to view this page.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/')}>Return home</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const handleDocumentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setDocumentFile(file);
    setStatus(null);
    setError(null);
  };

  const handleUploadDocument = async () => {
    if (!documentFile) {
      setError('Select a DOCX/TXT/MD/PDF file to continue.');
      return;
    }

    if (!isAllowedDoc(documentFile)) {
      setError('Only DOCX, TXT, MD, or PDF files are allowed.');
      return;
    }

    try {
      setIsUploadingFile(true);
      setError(null);
      setStatus(null);
      analytics.track('button: ui.admin_kb_upload_file', { fileName: documentFile.name });
      const source = await uploadKnowledgeDocument(documentFile);
      setStatus(`Queued ${source.displayName} for background ingestion.`);
      setDocumentFile(null);
      await loadSources();
    } catch (err) {
      logging.error('Knowledge document upload failed', { error: err });
      setError(err instanceof Error ? err.message : 'Document upload failed.');
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleUploadUrl = async () => {
    const trimmed = urlValue.trim();
    if (!trimmed) {
      setError('Enter a URL to continue.');
      return;
    }

    try {
      setIsUploadingUrl(true);
      setError(null);
      setStatus(null);
      analytics.track('button: ui.admin_kb_upload_url', { url: trimmed });
      const source = await uploadKnowledgeUrl(trimmed);
      setStatus(`Queued ${source.displayName} for background ingestion.`);
      setUrlValue('');
      await loadSources();
    } catch (err) {
      logging.error('Knowledge URL upload failed', { error: err });
      setError(err instanceof Error ? err.message : 'URL upload failed.');
    } finally {
      setIsUploadingUrl(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDeleteSource) {
      return;
    }

    try {
      setIsDeleting(true);
      setError(null);
      setStatus(null);
      analytics.track('button: ui.admin_kb_delete_source', { sourceId: pendingDeleteSource.id });
      const deleted = await deleteKnowledgeSource(pendingDeleteSource.id);
      setStatus(`Marked ${deleted.displayName} as deleted and removed its RAG data.`);
      setPendingDeleteSource(null);
      await loadSources();
    } catch (err) {
      logging.error('Knowledge source delete failed', { error: err, sourceId: pendingDeleteSource.id });
      setError(err instanceof Error ? err.message : 'Failed to delete source.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReingest = async (source: AdminKnowledgeSource) => {
    try {
      setReingestingIds((current) => new Set(current).add(source.id));
      setError(null);
      setStatus(null);
      analytics.track('button: ui.admin_kb_reingest_source', { sourceId: source.id });
      const updated = await reingestKnowledgeSource(source.id);
      setStatus(`Queued ${updated.displayName} for reingestion.`);
      await loadSources();
    } catch (err) {
      logging.error('Knowledge source reingest failed', { error: err, sourceId: source.id });
      setError(err instanceof Error ? err.message : 'Failed to reingest source.');
    } finally {
      setReingestingIds((current) => {
        const next = new Set(current);
        next.delete(source.id);
        return next;
      });
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Import data to vector DB</CardTitle>
          <CardDescription>
            Upload one document or one URL at a time. Each source is tracked below and ingested into RAG in background.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Document file (DOCX/TXT/MD/PDF)</label>
              <input
                type="file"
                accept=".docx,.txt,.md,.pdf"
                onChange={handleDocumentChange}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border file:border-border file:bg-background file:px-4 file:py-2 file:text-sm file:font-semibold file:text-foreground hover:file:bg-accent"
              />
              {documentFile ? (
                <p className="text-xs text-muted-foreground">Selected file: {documentFile.name}</p>
              ) : null}
            </div>
            <Button onClick={handleUploadDocument} disabled={isUploadingFile || !documentFile}>
              {isUploadingFile ? 'Uploading…' : 'Upload file'}
            </Button>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">HTML page URL</label>
              <input
                type="url"
                value={urlValue}
                onChange={(event) => {
                  setUrlValue(event.target.value);
                  setStatus(null);
                  setError(null);
                }}
                placeholder="https://example.com/page"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
              <p className="text-xs text-muted-foreground">
                The page will be fetched, text extracted, and ingested into RAG as a tracked source.
              </p>
            </div>
            <Button onClick={handleUploadUrl} disabled={isUploadingUrl || !urlValue.trim()}>
              {isUploadingUrl ? 'Uploading…' : 'Upload URL'}
            </Button>
          </div>

          {error ? <p className="text-sm text-destructive lg:col-span-2">{error}</p> : null}
          {status ? <p className="text-sm text-emerald-600 lg:col-span-2">{status}</p> : null}
        </CardContent>
        <CardFooter>
          <Button variant="outline" onClick={() => navigate('/')}>Back</Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Uploaded sources</CardTitle>
            <CardDescription>
              Track what was added to RAG, when it was uploaded, and whether the source is active, deleted, failed, or still processing.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              analytics.track('button: ui.admin_kb_refresh_sources');
              void loadSources();
            }}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Name / URL</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Uploaded at</th>
                  <th className="px-4 py-3 font-medium">Updated at</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.length === 0 ? (
                  <tr className="border-t">
                    <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                      No uploaded sources yet.
                    </td>
                  </tr>
                ) : (
                  sources.map((source) => {
                    const canDelete = source.status !== 'DELETED';
                    const canReingest = source.status === 'DELETED';
                    const isReingesting = reingestingIds.has(source.id);
                    return (
                      <tr key={source.id} className="border-t align-top">
                        <td className="px-4 py-3 font-medium">{source.sourceType}</td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <div className="font-medium text-foreground break-all">{source.displayName}</div>
                            <div className="text-xs text-muted-foreground break-all">{source.uniqueKey}</div>
                            {source.errorMessage ? (
                              <div className="text-xs text-destructive break-words">{source.errorMessage}</div>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClassName(source.status)}`}>
                            {source.status === 'IN_PROGRESS' ? 'IN PROGRESS' : source.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(source.createdAt)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(source.updatedAt)}</td>
                        <td className="px-4 py-3 text-right">
                          <DropdownMenu
                            open={activeActionSourceId === source.id}
                            onOpenChange={(open) => setActiveActionSourceId(open ? source.id : null)}
                          >
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[10rem] text-right">
                              <DropdownMenuItem
                                className="justify-end"
                                disabled={!canDelete}
                                onSelect={(event) => {
                                  event.preventDefault();
                                  if (!canDelete) {
                                    return;
                                  }
                                  setPendingDeleteSource(source);
                                  setActiveActionSourceId(null);
                                }}
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="justify-end"
                                disabled={!canReingest || isReingesting}
                                onSelect={(event) => {
                                  event.preventDefault();
                                  if (!canReingest || isReingesting) {
                                    return;
                                  }
                                  setActiveActionSourceId(null);
                                  void handleReingest(source);
                                }}
                              >
                                {isReingesting ? 'Reingesting…' : 'Reingest'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <ConfirmAlertDialog
        title="Delete source?"
        description={pendingDeleteSource
          ? `This will immediately remove the source "${pendingDeleteSource.displayName}" from RAG and mark its row as deleted.`
          : 'This will remove the source from RAG and mark it as deleted.'}
        confirmText={isDeleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={handleDelete}
        open={Boolean(pendingDeleteSource)}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setPendingDeleteSource(null);
          }
        }}
      />
    </div>
  );
};

export default AdminKnowledgeBasePage;
